import React, { Dispatch, SetStateAction, useState } from 'react';
import { View, Modal } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { translate } from '../../services/translate';
import MyAppText from '../../components/MyAppText';
import CustomButton, { ButtonTypes } from '../../components/CustomButton';
import { WindowInformation } from '../../services/window-information';
import MyDropDownPicker from '../../components/MyDropDownPicker';
import { MyCheckbox } from '../../components/MyCheckbox';


interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: (value: boolean) => void;
    onSubmit: (videoStorageProviderId: number) => void;
    onCancel?: () => void;
    itemName?: string;
    customMessage?: string;
    customConfirm?: string;
    onSubmitType?: ButtonTypes;
    isLoading?: boolean;
    maxWidth?: number;
    availableVideoStorageProviders: { id: number; name: string; }[];
    useTransferFile: boolean;
    setUseTransferFile: Dispatch<SetStateAction<boolean>>;
}

export default function UpdateVideoStorageProviderModal({
    isModalVisible,
    setModalVisible,
    onSubmit,
    onCancel,
    maxWidth,
    availableVideoStorageProviders,
    useTransferFile,
    setUseTransferFile,
}: ModalProps) {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [videoStorageProviderId, setVideoStorageProviderId] = useState<number | null>(null);

    return (
        <Modal
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => {
                onCancel && onCancel();
                setModalVisible(false);
            }}
            transparent={true}
        >
            <View style={styles.container}>
                <View style={styles.body(windowInfo.isMobile, maxWidth)}>
                    <MyAppText style={styles.confirmationText}>{translate('selectVideoStorageProvider')}</MyAppText>
                    <View style={{ zIndex: 3 }}>
                        <MyDropDownPicker
                            placeholder={translate('videoStorageProviders')}
                            searchable={true}
                            items={[{}, ...availableVideoStorageProviders.map(p => ({
                                value: p.id,
                                label: p.name,
                            }))]}
                            open={dropdownOpen}
                            setOpen={setDropdownOpen}
                            setValue={setVideoStorageProviderId}
                            value={videoStorageProviderId}
                            zIndex={3}
                        />
                    </View>
                    <MyCheckbox
                        label={translate('useTransferFile')}
                        checked={useTransferFile}
                        setChecked={setUseTransferFile}
                    />
                    <View style={styles.confirmButtonsView}>
                        <CustomButton
                            onPress={() => {
                                onCancel && onCancel();
                                setModalVisible(false);
                            }}
                            text={translate('no')}
                            type='secondary' />
                        <CustomButton
                            onPress={() => {
                                if (!videoStorageProviderId) {
                                    return;
                                }
                                onSubmit(videoStorageProviderId);
                                setModalVisible(false);
                            }}
                            disabled={!videoStorageProviderId}
                            type={'primary'}
                            text={translate('confirm')} />
                    </View>
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1
    },
    body: (isMobile: boolean, maxWidth?: number) => ({
        width: isMobile ? '95%' : '80%',
        maxWidth: maxWidth,
        backgroundColor: theme.colors.card.background,
        flexDirection: 'column',
        borderRadius: 4,
        padding: 25,
        rowGap: 20,
    }),
    headerText: {
        fontSize: 20,
    },
    buttonsView: {
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
        gap: 20
    },
    confirmationText: {
        fontSize: 20,
    },
    confirmButtonsView: {
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
    }
}));
